import Autoplay from 'embla-carousel-autoplay'
import Fade from 'embla-carousel-fade'
import Image from 'next/image'
import React from 'react'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem
} from '@/modules/components/ui/carousel'
import { getStoriesData } from '@/modules/home-page/widgets/Hero/fractals/api/getStoriesData'

export const CarouselHero = ({
  stories
}: {
  stories: Awaited<ReturnType<typeof getStoriesData>>
}) => {
  const [api, setApi] = React.useState<CarouselApi>()
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>()
  const autoplayRef = React.useRef<any>(null)

  const scrollTo = React.useCallback(
    (index: number) => {
      if (api) {
        if (autoplayRef.current) {
          autoplayRef.current.stop()
          autoplayRef.current.play()
        }
        api.scrollTo(index)
      }
    },
    [api]
  )

  const plugins = React.useMemo(
    () => [
      Fade(),
      Autoplay({
        delay: 4500,
        stopOnInteraction: false
      })
    ],
    []
  )

  React.useEffect(() => {
    const [fadePlugin, autoplayPlugin] = plugins
    if (autoplayPlugin && typeof autoplayPlugin === 'object') {
      autoplayRef.current = autoplayPlugin
    }
    setSelectedIndex(0)
  }, [plugins])

  React.useEffect(() => {
    if (!api) return

    const onSelect = () => {
      try {
        setSelectedIndex(api.selectedScrollSnap())
      } catch (error: unknown) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error in carousel select handler:', error)
        }
      }
    }

    api.on('select', onSelect)

    return () => {
      if (api) {
        api.off('select', onSelect)
      }
    }
  }, [api])

  return (
    <>
      <Carousel
        className='w-full h-[260px] relative md:h-full md:absolute top-0 left-0 md:z-[-1] rounded-[24px] md:rounded-[40px] xl:rounded-[64px] overflow-hidden '
        setApi={setApi}
        plugins={plugins}>
        <CarouselContent className='h-full'>
          {stories
            ?.sort((a, b) => (a.id || 0) - (b.id || 0))
            .map((el, index) => (
              <CarouselItem key={el.url} className='relative w-full h-full'>
                <Image
                  sizes='100vw'
                  priority={index === 0}
                  fill
                  unoptimized
                  style={{ objectFit: 'cover' }}
                  alt={`story_${index}`}
                  src={el.url ?? ''}
                />
              </CarouselItem>
            ))}
        </CarouselContent>
        <div className='bg-[#0000001A] absolute w-full h-full left-0 top-0 hidden md:block' />
        <div className='absolute flex justify-center bottom-[20px] left-0 w-full gap-[7px] md:hidden '>
          {stories?.map((element, index) => (
            <button
              key={element.url}
              type='button'
              onClick={() => scrollTo(index)}
              className={`transition-colors bg-white/50 w-[10px] h-[10px] leading-[0] rounded-[10px]  ${
                index === selectedIndex ? 'w-[60px]' : ''
              } overflow-hidden`}>
              <span
                style={
                  index === selectedIndex
                    ? { transform: 'translate(1px)' }
                    : { transform: 'translate(-60px)' }
                }
                className={`inline-block w-[60px] translate-x-[-60px] bg-white h-full rounded-[10px] ${
                  index === selectedIndex
                    ? 'translate-x-[1px] duration-dots ease-out'
                    : ''
                }`}
              />
            </button>
          ))}
        </div>
      </Carousel>
      <div className='absolute justify-center  left-0 w-full gap-[7px] hidden xl:flex  xl:justify-end  xl:bottom-[70px] xl:pr-[60px]'>
        {stories?.map((element, index) => (
          <button
            key={element.url}
            type='button'
            onClick={() => scrollTo(index)}
            className={`transition-colors bg-white/50 w-[10px] h-[10px] leading-[0] rounded-[10px]  ${
              index === selectedIndex ? 'w-[60px]' : ''
            } overflow-hidden`}>
            <span
              style={
                index === selectedIndex
                  ? { transform: 'translate(1px)' }
                  : { transform: 'translate(-60px)' }
              }
              className={`inline-block w-[60px] translate-x-[-60px] bg-white h-full rounded-[10px] ${
                index === selectedIndex
                  ? 'translate-x-[1px] duration-dots ease-out'
                  : ''
              }`}
            />
          </button>
        ))}
      </div>
    </>
  )
}
